import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  type SignInOutput,
  signIn,
  signUp,
} from 'aws-amplify/auth';
import QRCode from 'qrcode';
import { type FormEvent, useEffect, useState } from 'react';
import welcome from './art/welcome.svg';
import { Brand } from './Logo.js';

const TITLES: Record<Step['kind'], string> = {
  password: 'Sign in',
  signup: 'Create your account',
  'confirm-email': 'Check your email',
  forgot: 'Reset your password',
  reset: 'Choose a new password',
  'new-password': 'Choose a new password',
  'setup-app': 'Set up two-step sign-in',
  code: 'Enter your code',
};

type Step =
  | { kind: 'password' }
  | { kind: 'signup' }
  | { kind: 'confirm-email'; password: string }
  | { kind: 'forgot' }
  | { kind: 'reset' }
  | { kind: 'new-password' }
  | { kind: 'setup-app'; uri: string; secret: string }
  | { kind: 'code' };

function nextStepOf(out: SignInOutput, email: string): Step | 'done' {
  const step = out.nextStep;
  switch (step.signInStep) {
    case 'DONE':
      return 'done';
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { kind: 'new-password' };
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
      return {
        kind: 'setup-app',
        uri: step.totpSetupDetails.getSetupUri('Foderis', email).toString(),
        secret: step.totpSetupDetails.sharedSecret,
      };
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { kind: 'code' };
    case 'CONFIRM_SIGN_UP':
      return { kind: 'confirm-email', password: '' };
    default:
      throw new Error('This sign-in step is not supported yet. Please contact support.');
  }
}

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  // foderis.com links here with ?signup to start a free trial.
  const [step, setStep] = useState<Step>(
    new URLSearchParams(window.location.search).has('signup')
      ? { kind: 'signup' }
      : { kind: 'password' },
  );
  const [email, setEmail] = useState('');
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [qr, setQr] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (step.kind === 'setup-app')
      void QRCode.toDataURL(step.uri, { margin: 1, width: 200 }).then(setQr);
  }, [step]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNote('');
    try {
      const username = email.trim().toLowerCase();
      let out: SignInOutput;
      if (step.kind === 'signup') {
        // Cognito emails a code; MFA is set up on the first sign-in, as for everyone.
        const up = await signUp({
          username,
          password: value,
          options: { userAttributes: { email: username } },
        });
        if (up.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
          setStep({ kind: 'confirm-email', password: value });
          setValue('');
          return;
        }
        out = await signIn({ username, password: value });
      } else if (step.kind === 'confirm-email') {
        await confirmSignUp({ username, confirmationCode: code.trim() });
        setCode('');
        if (!step.password) {
          setStep({ kind: 'password' });
          setNote('Email confirmed. Sign in to continue.');
          return;
        }
        out = await signIn({ username, password: step.password });
      } else if (step.kind === 'forgot') {
        await resetPassword({ username });
        setStep({ kind: 'reset' });
        setNote('We emailed you a code.');
        return;
      } else if (step.kind === 'reset') {
        await confirmResetPassword({ username, confirmationCode: code.trim(), newPassword: value });
        setCode('');
        out = await signIn({ username, password: value });
      } else if (step.kind === 'password') {
        out = await signIn({ username, password: value });
      } else {
        out = await confirmSignIn({ challengeResponse: value.trim() });
      }
      const next = nextStepOf(out, username);
      setValue('');
      if (next === 'done') onSignedIn();
      else setStep(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError('');
    try {
      await resendSignUpCode({ username: email.trim().toLowerCase() });
      setNote('We sent a new code.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send a new code.');
    }
  }

  return (
    <div className="auth">
      <aside className="auth-art" aria-hidden="true">
        <Brand light />
        <img src={welcome} alt="" width={440} height={342} />
        <p>Your books, done in under 5 minutes a month. Hosted in Australia.</p>
      </aside>
      <form className="auth-form" onSubmit={submit}>
        <h1>{TITLES[step.kind]}</h1>
        {(step.kind === 'signup' || step.kind === 'forgot') && (
          <p>
            {step.kind === 'signup'
              ? 'Start your 30-day free trial. No card needed.'
              : 'We will email you a code to choose a new one.'}
          </p>
        )}
        {(step.kind === 'password' || step.kind === 'signup' || step.kind === 'forgot') && (
          <>
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            {step.kind !== 'forgot' && (
              <label>
                {step.kind === 'signup' ? 'Choose a password (at least 12 characters)' : 'Password'}
                <input
                  type="password"
                  autoComplete={step.kind === 'signup' ? 'new-password' : 'current-password'}
                  minLength={step.kind === 'signup' ? 12 : undefined}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  required
                />
              </label>
            )}
          </>
        )}
        {(step.kind === 'confirm-email' || step.kind === 'reset') && (
          <label>
            {step.kind === 'confirm-email'
              ? `The code we emailed to ${email}`
              : 'The code we emailed you'}
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </label>
        )}
        {step.kind === 'reset' && (
          <label>
            New password (at least 12 characters)
            <input
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </label>
        )}
        {step.kind === 'new-password' && (
          <label>
            Choose a new password (at least 12 characters)
            <input
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </label>
        )}
        {step.kind === 'setup-app' && (
          <>
            <p>
              Scan this with an authenticator app (1Password, Google Authenticator, Authy), then
              enter the 6-digit code.
            </p>
            {qr && <img src={qr} alt="Authenticator setup code" width={200} height={200} />}
            <p className="muted">
              Can't scan? Enter this key: <code>{step.secret}</code>
            </p>
          </>
        )}
        {(step.kind === 'setup-app' || step.kind === 'code') && (
          <label>
            6-digit code from your authenticator app
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </label>
        )}
        {note && <p>{note}</p>}
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={busy}>
          {busy ? 'One moment' : step.kind === 'signup' ? 'Start free trial' : 'Continue'}
        </button>
        <div className="title-row wrap">
          {step.kind === 'password' && (
            <>
              <button type="button" className="link" onClick={() => setStep({ kind: 'signup' })}>
                Create an account
              </button>
              <button type="button" className="link" onClick={() => setStep({ kind: 'forgot' })}>
                Forgot password
              </button>
            </>
          )}
          {(step.kind === 'signup' || step.kind === 'forgot') && (
            <button type="button" className="link" onClick={() => setStep({ kind: 'password' })}>
              I have an account
            </button>
          )}
          {step.kind === 'confirm-email' && (
            <button type="button" className="link" onClick={resend}>
              Send a new code
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
